import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';
import printing from 'assets/lottie/printing';
import { FlexBox, Box, Typography, Button, Grid, SvgIcon, MuiBox } from '@kis-technologies/kis-ui';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'services/hardware';
import { selectors as kioskSelectors } from 'services/kiosk';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { ActionOverlay } from 'components/ActionOverlay';
import { useStyles } from './styled';

// Lottie Animation Config
const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: printing,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid meet'
  }
};

const PrintingView: React.FC = () => {
  const navigate = useNavigate();
  // Action Creators and Selectors
  const printData = useSelector(hardwareSelectors.selectPrintData);
  const kioskLoading = useSelector(kioskSelectors.selectLoading);
  const deliveryStatus = useSelector(ticketingSelectors.selectDeliveryStatus);

  const { printAllJobs, resetDelivery } = useActions({
    printAllJobs: hardwareActions.printAllJobs,
    resetDelivery: ticketingActions.resetDelivery
  });

  const classes = useStyles();

  useEffect(() => {
    printAllJobs();
  }, []);

  const onBackToMaintenance = () => {
    resetDelivery();
    navigate('/maintenance');
  };

  const isReceipt = () => {
    if (
      typeof printData.count === 'number' &&
      printData.printJobs.length &&
      printData.printJobs[printData.count as number].ticketNumber === 'receipt'
    ) {
      return true;
    }
    return false;
  };

  const countOfReceipts = printData.printJobs.filter((job) => job.ticketNumber === 'receipt').length;
  const receiptText = `Printing ${countOfReceipts > 1 ? 'Receipts' : 'Receipt'} `;
  const ticketText = `Printing Ticket \n ${(printData.count as number) + 1} of ${printData.total && printData.total - countOfReceipts} `;

  return (
    <>
      <Grid className={classes.printingOuterContainer}>
        <Grid container className={classes.printingInnerContainer}>
          <Grid item lg={12} xl={6} className={classes.printingTextContainer}>
            {/* Show printing content until the delivery completes */}
            {deliveryStatus !== 'completed' ? (
              <MuiBox className={classes.printingTextBox}>
                <Grid className={classes.printingHeaderContainer}>
                  <Typography
                    fontSize={100}
                    fontWeight={600}
                    sx={{ lineHeight: 1.1, whiteSpace: 'pre-wrap' }}
                    align="center"
                  >
                    {isReceipt() ? receiptText : ticketText}
                    <SvgIcon name="down_circle" size={70} />
                  </Typography>
                </Grid>
                <Typography fontSize={30}>Look below for your tickets</Typography>
              </MuiBox>
            ) : (
              <Box width={610} flexOverride="none" margin="0 0 9" sx={{ textAlign: 'center' }}>
                <FlexBox margin="0 0 8">
                  <Typography fontSize={100} fontWeight={600} sx={{ lineHeight: 1.1 }}>
                    Printing Complete <SvgIcon name="down_circle" size={70} />
                  </Typography>
                </FlexBox>
                <Box flexOverride="none" margin="0 0 8">
                  <Typography fontSize={30}>Look below for your tickets</Typography>
                </Box>
                <Button
                  customSize="giant"
                  appearance="primary"
                  width={475}
                  onClick={onBackToMaintenance}
                  isLoading={kioskLoading}
                  data-testid="back-to-maintenance-button"
                >
                  Back to Maintenance
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item lg={12} xl={6} className={classes.printingImageContainer}>
            <FlexBox fullHeight padding="0 20" align="center" justify="center">
              <Lottie options={lottieOptions} isClickToPauseDisabled />
            </FlexBox>
          </Grid>
        </Grid>
      </Grid>

      {/* Error overlay if the delivery failed */}
      {deliveryStatus === 'failed' && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={800}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                Looks like something went wrong.
              </Typography>
            </Box>
          }
          description="Looks like something went wrong while printing."
          cancelText="Go Back"
          onCancel={onBackToMaintenance}
        />
      )}
    </>
  );
};

export default PrintingView;
